import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import Footer from '../components/Footer'
import { useUser } from '../contexts/UserContext'
import { queries } from '../utils/queryOptimizer'
import { TableSkeleton } from '../components/LoadingSkeleton'
import { ArrowLeft } from 'lucide-react'
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function ManageUsers() {
  const { userInfo, loading: userLoading } = useUser()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState({ message: '', type: '' })
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all') // 'all', 'admin', 'user'
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'pending', 'approved', 'rejected'
  const [currentPage, setCurrentPage] = useState(1)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' })
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const usersPerPage = 10
  const navigate = useNavigate()

  useEffect(() => {
    if (!userLoading && (!userInfo || userInfo.role !== 'admin')) {
      navigate('/dashboard')
    }
  }, [userLoading, userInfo, navigate])

  useEffect(() => {
    const fetchAllUsers = async () => {
      if (!userInfo || userInfo.role !== 'admin') return
      
      setLoading(true)
      try {
        const data = await queries.getAllUsers()
        if (data) setUsers(data)
      } catch (error) {
        console.error('Error loading users:', error)
        showToast('Gagal memuat data users', 'error')
      } finally {
        setLoading(false)
      }
    }

    if (userInfo?.role === 'admin') {
      fetchAllUsers()
    }
  }, [userInfo])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast({ message: '', type: '' }), 3000)
  }

  const handleRoleChange = async (id, newRole) => {
    const { error } = await supabase.from('users').update({ role: newRole }).eq('id', id)
    if (error) return showToast('Gagal mengubah role', 'error')

    setUsers((prev) =>
      prev.map((user) => (user.id === id ? { ...user, role: newRole } : user))
    )
    showToast('Role berhasil diubah')
  }

  const handleSort = (key) => {
    let direction = 'ascending'
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending'
    }
    setSortConfig({ key, direction })
  }

  const sortedUsers = [...users]
    .filter((user) => {
      // Filter by search term
      const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by role
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      
      // Filter by status
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      
      return matchesSearch && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0
      const aVal = a[sortConfig.key]?.toLowerCase?.() || ''
      const bVal = b[sortConfig.key]?.toLowerCase?.() || ''
      if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1
      return 0
    })

    const handleResetPassword = async (userId, userEmail) => {
    // Admin set password langsung
    const newPassword = prompt(`Masukkan password baru untuk ${userEmail}:`)
    if (!newPassword) return

    if (newPassword.length < 6) {
      showToast('Password minimal 6 karakter', 'error')
      return
    }

    const confirmPassword = prompt('Konfirmasi password baru:')
    if (newPassword !== confirmPassword) {
      showToast('Password tidak cocok', 'error')
      return
    }

    try {
      // Call RPC function to update password
      const { error } = await supabase.rpc('update_user_password', {
        target_user_id: userId,
        new_password: newPassword
      })

      if (error) throw error

      showToast(`✅ Password untuk ${userEmail} berhasil diubah`, 'success')
    } catch (error) {
      console.error('Reset password error:', error)
      showToast('Gagal mengubah password: ' + error.message, 'error')
    }
  }

  const handleViewDetail = async (user) => {
    setSelectedUser(user)
    setShowDetailModal(true)
  }

  const handleApproveUser = async (userId, userEmail) => {
    const confirmApprove = window.confirm(
      `Apakah Anda yakin ingin menyetujui akun ${userEmail}?`
    )
    if (!confirmApprove) return

    try {
      const { error } = await supabase
        .from('users')
        .update({
          status: 'approved',
          approved_by: userInfo.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', userId)

      if (error) throw error

      // Update local state
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? { ...user, status: 'approved', approved_by: userInfo.id, approved_at: new Date().toISOString() }
            : user
        )
      )

      showToast(`✅ Akun ${userEmail} telah disetujui`, 'success')
    } catch (error) {
      console.error('Approve error:', error)
      showToast('Gagal menyetujui akun: ' + error.message, 'error')
    }
  }

  const handleRejectUser = async (userId, userEmail) => {
    const reason = prompt(`Masukkan alasan penolakan untuk ${userEmail}:`)
    if (reason === null) return // User cancelled

    const confirmReject = window.confirm(
      `⚠️ PERHATIAN: Akun ${userEmail} akan DIHAPUS PERMANEN dari database. Lanjutkan?`
    )
    if (!confirmReject) return

    try {
      // Log rejection to user_deletions table
      const { error: logError } = await supabase
        .from('user_deletions')
        .insert({
          user_id: userId,
          user_email: userEmail,
          user_name: userEmail.split('@')[0],
          deleted_by: userInfo.id,
          deleted_at: new Date().toISOString(),
          reason: `Rejected: ${reason || 'Tidak memenuhi kriteria'}`
        })

      if (logError) {
        console.error('Error logging rejection:', logError)
      }

      // Delete from public.users
      const { error: deleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId)

      if (deleteError) throw deleteError

      // Delete from auth.users
      const { error: authDeleteError } = await supabase.rpc('delete_user', { user_id: userId })
      
      if (authDeleteError) {
        console.error('Error deleting from auth:', authDeleteError)
      }

      // Update local state
      setUsers(prev => prev.filter(user => user.id !== userId))

      showToast(`❌ Akun ${userEmail} ditolak dan dihapus`, 'success')
    } catch (error) {
      console.error('Reject error:', error)
      showToast('Gagal menolak akun: ' + error.message, 'error')
    }
  }

  const handleDelete = async (id) => {
    if (id === userInfo.id) {
      showToast('Anda tidak bisa menghapus akun Anda sendiri', 'error')
      return
    }

    const confirmDelete = window.confirm('Apakah Anda yakin ingin menghapus pengguna ini?')
    if (!confirmDelete) return

    try {
      
      // Get user data before deletion
      const { data: userData, error: fetchError } = await supabase
        .from('users')
        .select('email, full_name')
        .eq('id', id)

      if (fetchError) {
        console.error('❌ Error fetching user data:', fetchError)
        throw new Error('Gagal mengambil data user: ' + fetchError.message)
      }

      // Check if user exists
      if (!userData || userData.length === 0) {
        throw new Error('User tidak ditemukan')
      }

      const userToDelete = userData[0]

      // Insert to user_deletions table for tracking - THIS IS CRITICAL
      const { data: insertData, error: insertError } = await supabase
        .from('user_deletions')
        .insert({
          user_id: id,
          user_email: userToDelete.email,
          user_name: userToDelete.full_name,
          deleted_by: userInfo.id,
          deleted_at: new Date().toISOString()
        })
        .select()

      if (insertError) {
        console.error('❌ CRITICAL: Error inserting to user_deletions:', insertError)
        console.error('Error details:', {
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint
        })
        
        // STOP the delete process if we can't track it
        throw new Error('⚠️ Tidak bisa mencatat penghapusan ke database. Error: ' + insertError.message + '. Cek RLS policy di Supabase!')
      }


      // Delete from users table
      const { error: deleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', id)

      if (deleteError) {
        console.error('❌ Error deleting from users table:', deleteError)
        throw new Error('Gagal menghapus dari tabel users: ' + deleteError.message)
      }


      // Delete from Supabase Auth (requires admin privileges)
      const { error: authDeleteError } = await supabase.rpc('delete_user', { user_id: id })
      
      if (authDeleteError) {
        console.error('⚠️ Error deleting from auth:', authDeleteError)
        // Don't throw error, just log it - user is already deleted from users table
        showToast('User dihapus dari database, tapi masih ada di auth. Hubungi admin.', 'warning')
      } else {
      }

      setUsers((prev) => prev.filter((user) => user.id !== id))
      showToast('✅ Pengguna berhasil dihapus dan tercatat!', 'success')
      
    } catch (error) {
      console.error('❌ Delete error:', error)
      showToast('Gagal menghapus pengguna: ' + error.message, 'error')
    }
  }

  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  )

  const totalPages = Math.ceil(sortedUsers.length / usersPerPage)

  // Calculate statistics
  const stats = {
    total: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    user: users.filter(u => u.role === 'user').length,
    pending: users.filter(u => u.status === 'pending').length,
    approved: users.filter(u => u.status === 'approved').length,
    rejected: users.filter(u => u.status === 'rejected').length,
    filtered: sortedUsers.length
  }

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Floating Gradient Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-blue-500/30 via-blue-500/30 to-blue-500/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] bg-gradient-to-bl from-blue-500/30 via-blue-500/30 to-blue-500/30 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/20 via-blue-500/20 to-blue-500/20 rounded-full blur-3xl animate-float-slow"></div>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(139, 92, 246, 0.02) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">

          {toast.message && (
            <div
              className={`px-4 py-3 rounded-lg shadow-md text-sm font-medium ${
                toast.type === 'error'
                  ? 'bg-red-100 text-red-700 border border-rose-300'
                  : 'bg-green-100 text-green-700 border border-green-300'
              }`}
            >
              {toast.message}
            </div>
          )}
          
          {/* Header Banner */}
          <div className="relative backdrop-blur-xl bg-gradient-to-r from-blue-600/90 via-blue-600/90 to-blue-600/90 rounded-3xl shadow-xl p-6 lg:p-8 text-white border border-white/20 overflow-hidden">
            {/* Shimmer Effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 rounded-lg shadow-md transition-all"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold">User Management</h1>
                  <p className="text-blue-100 text-sm mt-1">Kelola pengguna dan permissions sistem</p>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="text-3xl mb-2">👥</div>
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <div className="text-sm text-blue-100">Total Users</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="text-3xl mb-2">⏳</div>
                  <div className="text-2xl font-bold">{stats.pending}</div>
                  <div className="text-sm text-blue-100">Pending Approval</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-2xl font-bold">{stats.approved}</div>
                  <div className="text-sm text-blue-100">Approved</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                  <div className="text-3xl mb-2">🔍</div>
                  <div className="text-2xl font-bold">{stats.filtered}</div>
                  <div className="text-sm text-blue-100">Filtered Results</div>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="relative backdrop-blur-xl bg-white/70 rounded-2xl shadow-xl border border-white/40 overflow-hidden">
            {/* Shimmer Effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
            
            <div className="relative z-10 p-6 space-y-4">
              {/* Search Input */}
              <div className="flex items-center gap-3">
                <div className="text-blue-600 text-2xl">🔍</div>
                <input
                  type="text"
                  placeholder="Cari berdasarkan nama atau email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-3 border-0 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-300 text-sm transition-all"
                />
              </div>

              {/* Role Filter Buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-600">Filter by Role:</span>
                <button
                  onClick={() => {
                    setRoleFilter('all')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    roleFilter === 'all'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All Users ({users.length})
                </button>
                <button
                  onClick={() => {
                    setRoleFilter('admin')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    roleFilter === 'admin'
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  👑 Admin ({stats.admin})
                </button>
                <button
                  onClick={() => {
                    setRoleFilter('user')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    roleFilter === 'user'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  👤 User ({stats.user})
                </button>
              </div>

              {/* Status Filter Buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-600">Filter by Status:</span>
                <button
                  onClick={() => {
                    setStatusFilter('all')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === 'all'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All Status ({users.length})
                </button>
                <button
                  onClick={() => {
                    setStatusFilter('pending')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === 'pending'
                      ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ⏳ Pending ({stats.pending})
                </button>
                <button
                  onClick={() => {
                    setStatusFilter('approved')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === 'approved'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ✅ Approved ({stats.approved})
                </button>
                <button
                  onClick={() => {
                    setStatusFilter('rejected')
                    setCurrentPage(1)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === 'rejected'
                      ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ❌ Rejected ({stats.rejected})
                </button>
                
                {/* Active Filter Indicator */}
                {(roleFilter !== 'all' || statusFilter !== 'all') && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                    <span>Showing: {sortedUsers.length} results</span>
                    <button
                      onClick={() => {
                        setRoleFilter('all')
                        setStatusFilter('all')
                        setCurrentPage(1)
                      }}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      ✕ Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <TableSkeleton rows={10} />
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block relative backdrop-blur-xl bg-white/70 shadow-xl rounded-2xl border border-white/40 overflow-hidden">
                {/* Shimmer Effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
                
                <div className="overflow-x-auto relative z-10">
                  <table className="min-w-full text-sm divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-slate-600">
                      <tr>
                        <th
                          className="px-6 py-3 text-left font-medium cursor-pointer hover:text-blue-600"
                          onClick={() => handleSort('full_name')}
                        >
                          Nama {sortConfig.key === 'full_name' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                        </th>
                        <th
                          className="px-6 py-3 text-left font-medium cursor-pointer hover:text-blue-600"
                          onClick={() => handleSort('email')}
                        >
                          Email {sortConfig.key === 'email' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                        </th>
                        <th
                          className="px-6 py-3 text-left font-medium cursor-pointer hover:text-blue-600"
                          onClick={() => handleSort('role')}
                        >
                          Role {sortConfig.key === 'role' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                        </th>
                        <th
                          className="px-6 py-3 text-left font-medium cursor-pointer hover:text-blue-600"
                          onClick={() => handleSort('status')}
                        >
                          Status {sortConfig.key === 'status' ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                        </th>
                        <th className="px-6 py-3 text-left font-medium">Ubah Role</th>
                        <th className="px-6 py-3 text-center font-medium">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {paginatedUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-slate-800">{user.full_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-slate-600">{user.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'admin' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.role === 'admin' ? '👑 Admin' : '👤 User'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.status === 'approved' 
                                ? 'bg-green-100 text-green-800' 
                                : user.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.status === 'approved' ? '✅ Approved' : user.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={user.role}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              className="border border-gray-300 rounded px-3 py-1 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                              {/* Approval Buttons - Only show for pending */}
                              {user.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApproveUser(user.id, user.email)}
                                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-xs shadow transition"
                                    title="Approve User"
                                  >
                                    ✓ Approve
                                  </button>
                                  <button
                                    onClick={() => handleRejectUser(user.id, user.email)}
                                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs shadow transition"
                                    title="Reject User"
                                  >
                                    ✕ Reject
                                  </button>
                                </>
                              )}
                              
                              {/* Regular action buttons */}
                              <button
                                onClick={() => handleViewDetail(user)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs shadow transition"
                                title="View Detail"
                              >
                                Lihat
                              </button>
                              <button
                                onClick={() => navigate(`/edit-profile?userId=${user.id}`)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs shadow transition"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleResetPassword(user.id, user.email)}
                                className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-xs shadow transition"
                                title="Set New Password"
                              >
                                Set PW
                              </button>
                              <button
                                onClick={() => handleDelete(user.id)}
                                className="bg-rose-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs shadow transition"
                              >
                                Hapus
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {paginatedUsers.map((user) => (
                  <div key={user.id} className="relative backdrop-blur-xl bg-white/70 rounded-2xl shadow-xl border border-white/40 p-4 overflow-hidden">
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>
                    
                    <div className="space-y-3 relative z-10">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Nama</p>
                        <p className="text-sm font-semibold text-slate-800">{user.full_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Email</p>
                        <p className="text-sm text-slate-600">{user.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Role</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role === 'admin' ? '👑 Admin' : '👤 User'}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Ubah Role</p>
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      
                      {/* Status Badge */}
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'approved' 
                            ? 'bg-green-100 text-green-800' 
                            : user.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.status === 'approved' ? '✅ Approved' : user.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                        </span>
                      </div>
                      
                      {/* Approve/Reject Buttons for Pending Users */}
                      {user.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveUser(user.id, user.email)}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm shadow transition font-medium"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => handleRejectUser(user.id, user.email)}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm shadow transition font-medium"
                          >
                            ✕ Reject
                          </button>
                        </div>
                      )}
                      
                      {/* Regular Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleViewDetail(user)}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm shadow transition font-medium"
                        >
                          👁 Detail
                        </button>
                        <button
                          onClick={() => navigate(`/edit-profile?userId=${user.id}`)}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm shadow transition font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleResetPassword(user.id, user.email)}
                          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg text-sm shadow transition font-medium"
                        >
                          Reset PW
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="flex-1 bg-rose-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm shadow transition font-medium"
                        >
                          Hapus
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-4 bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-white/40 mt-4">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="w-full sm:w-auto px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400 transition font-medium"
                >
                  ← Sebelumnya
                </button>
                <span className="text-sm text-slate-700 font-medium">
                  Halaman {currentPage} dari {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="w-full sm:w-auto px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400 transition font-medium"
                >
                  Selanjutnya →
                </button>
              </div>
          </>
        )}

        </div>
      </div>

      {/* Modal Detail User */}
      {showDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-600 p-6 text-white flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Detail Profil User</h2>
                <p className="text-blue-100 text-sm mt-1">Informasi lengkap user</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Avatar & Basic Info */}
              <div className="flex flex-col items-center text-center pb-6 border-b border-gray-200">
                {selectedUser.avatar_url ? (
                  <img
                    src={selectedUser.avatar_url}
                    alt={selectedUser.full_name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-blue-100 shadow-lg mb-4"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-4">
                    {selectedUser.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900">{selectedUser.full_name}</h3>
                <p className="text-gray-500 text-sm mt-1">{selectedUser.email}</p>
                <div className="flex gap-2 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedUser.role === 'admin' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedUser.role === 'admin' ? '👑 Admin' : '👤 User'}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedUser.status === 'approved' 
                      ? 'bg-green-100 text-green-800' 
                      : selectedUser.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedUser.status === 'approved' ? '✅ Approved' : selectedUser.status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                  </span>
                </div>
              </div>

              {/* Detail Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 font-medium">📞 Nomor Telepon</p>
                  <p className="text-sm text-gray-900 font-medium">{selectedUser.phone_number || '-'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 font-medium">🎂 Tanggal Lahir</p>
                  <p className="text-sm text-gray-900 font-medium">
                    {selectedUser.date_of_birth ? new Date(selectedUser.date_of_birth).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 font-medium">🎓 Pendidikan</p>
                  <p className="text-sm text-gray-900 font-medium">{selectedUser.education_level || '-'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 font-medium">💼 Divisi</p>
                  <p className="text-sm text-gray-900 font-medium">{selectedUser.division || '-'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 font-medium">📍 Posisi</p>
                  <p className="text-sm text-gray-900 font-medium">{selectedUser.position || '-'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1 font-medium">📅 Join Date</p>
                  <p className="text-sm text-gray-900 font-medium">
                    {selectedUser.join_date ? new Date(selectedUser.join_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                  </p>
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500 mb-2 font-medium">🏠 Alamat</p>
                <p className="text-sm text-gray-900">{selectedUser.address || '-'}</p>
              </div>

              {/* Bio */}
              {selectedUser.bio && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2 font-medium">📝 Bio</p>
                  <p className="text-sm text-gray-900">{selectedUser.bio}</p>
                </div>
              )}

              {/* Skills */}
              {selectedUser.skills && selectedUser.skills.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2 font-medium">💡 Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.skills.map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Info */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600 mb-2 font-bold">ℹ️ Informasi Akun</p>
                <div className="space-y-1 text-xs text-gray-700">
                  <p><span className="font-semibold">User ID:</span> {selectedUser.id}</p>
                  <p><span className="font-semibold">Dibuat:</span> {new Date(selectedUser.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  {selectedUser.approved_at && (
                    <p><span className="font-semibold">Disetujui:</span> {new Date(selectedUser.approved_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 flex gap-3">
              <button
                onClick={() => {
                  setShowDetailModal(false)
                  navigate(`/edit-profile?userId=${selectedUser.id}`)
                }}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                ✏️ Edit Profil
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium transition"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}
